import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { shouldExcludeArtist } from '@/utils/blacklist';
import { Tour } from '@/types';
import {
  getWeeklySeed,
  seededShuffle,
  isFestivalTour,
  getFreshnessBonus,
  limitPerArtist,
} from '@/utils/tourScoring';
import { deduplicateTours } from '@/utils/tourNormalization';

/**
 * Fetches featured artists with improved curation algorithm.
 */
export const useFeaturedArtists = () => {
  return useQuery({
    queryKey: ['featured-artists'],
    queryFn: async () => {
      try {
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

        const { data: artists, error: artistsError } = await supabase
          .from('artists')
          .select('id, name, image, genre, spotify_id')
          .eq('is_hidden', false)
          .limit(500);

        if (artistsError || !artists?.length) return [];

        // Get engagement data in parallel
        const today = new Date().toISOString().split('T')[0];
        const [
          { data: recentFollows },
          { data: recentReviews },
          { data: upcomingConcerts },
          { data: allTours },
        ] = await Promise.all([
          supabase.from('artist_followers').select('artist_id').gte('created_at', thirtyDaysAgo.toISOString()),
          supabase.from('reviews').select('artist_name').eq('status', 'published').gte('created_at', thirtyDaysAgo.toISOString()),
          supabase.from('concerts').select('artist_id').gte('date', today),
          // Batch in chunks to avoid URL length limits
          (async () => {
            const ids = artists.map(a => a.id);
            const CHUNK = 100;
            const results = await Promise.all(
              Array.from({ length: Math.ceil(ids.length / CHUNK) }, (_, i) =>
                supabase.from('tours').select('artist_id, name').in('artist_id', ids.slice(i * CHUNK, (i + 1) * CHUNK))
              )
            );
            return { data: results.flatMap(r => r.data || []) };
          })(),
        ]);

        // Count engagement
        const followCounts = new Map<string, number>();
        (recentFollows || []).forEach(f => followCounts.set(f.artist_id, (followCounts.get(f.artist_id) || 0) + 1));

        const artistNameToId = new Map<string, string>();
        artists.forEach(a => artistNameToId.set(a.name.toLowerCase(), a.id));
        const reviewCounts = new Map<string, number>();
        (recentReviews || []).forEach(r => {
          const id = artistNameToId.get(r.artist_name?.toLowerCase() || '');
          if (id) reviewCounts.set(id, (reviewCounts.get(id) || 0) + 1);
        });

        const hasUpcomingTour = new Set<string>();
        (upcomingConcerts || []).forEach(c => { if (c.artist_id) hasUpcomingTour.add(c.artist_id); });

        const tourCounts = new Map<string, number>();
        const toursByArtist = new Map<string, Set<string>>();
        (allTours || []).forEach(t => {
          if (!t.artist_id) return;
          const existing = toursByArtist.get(t.artist_id) || new Set();
          existing.add(t.name);
          toursByArtist.set(t.artist_id, existing);
        });
        toursByArtist.forEach((tours, artistId) => tourCounts.set(artistId, tours.size));

        // Score and rank with improved algorithm
        const scoredArtists = artists
          .filter(a => !shouldExcludeArtist(a.name))
          .map(artist => {
            const followScore = (followCounts.get(artist.id) || 0) * 15;
            const reviewScore = (reviewCounts.get(artist.id) || 0) * 20;
            const tourBonus = hasUpcomingTour.has(artist.id) ? 80 : 0;
            const spotifyBonus = artist.spotify_id ? 10 : 0;
            // New scoring signals
            const concertBonus = Math.min((tourCounts.get(artist.id) || 0) * 20, 100);
            const imageQualityBonus = artist.image?.includes('i.scdn.co') ? 20 : 0;
            const genreBonus = artist.genre?.length ? 15 : 0;

            const totalScore = followScore + reviewScore + tourBonus + spotifyBonus + concertBonus + imageQualityBonus + genreBonus;

            return {
              id: artist.id,
              name: artist.name,
              image: artist.image || '',
              genre: artist.genre || [],
              concertCount: tourCounts.get(artist.id) || 0,
              followers: followCounts.get(artist.id) || 0,
              hasUpcomingTour: hasUpcomingTour.has(artist.id),
              _score: totalScore,
            };
          })
          .sort((a, b) => {
            if (b._score !== a._score) return b._score - a._score;
            if (a.hasUpcomingTour !== b.hasUpcomingTour) return a.hasUpcomingTour ? -1 : 1;
            return a.name.localeCompare(b.name);
          });

        const topArtists = scoredArtists.slice(0, 15);
        const restArtists = scoredArtists.slice(15);
        const shuffledRest = seededShuffle(restArtists, getWeeklySeed());

        return [...topArtists, ...shuffledRest].slice(0, 50);
      } catch (error) {
        console.error('Featured artists query failed:', error);
        return [];
      }
    },
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Fetches featured tours — no Spotify image resolution to avoid rate limits.
 */
export const useFeaturedTours = () => {
  return useQuery({
    queryKey: ['featured-tours'],
    queryFn: async () => {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const today = new Date().toISOString().split('T')[0];

      const { data: tours, error: toursError } = await supabase
        .from('tours')
        .select(`id, name, slug, artist_id, year, average_rating, concert_count, image, created_at, artists ( id, name, image, genre )`)
        .limit(500);

      if (toursError) throw toursError;
      if (!tours?.length) return [];

      const { data: allConcerts } = await supabase.from('concerts').select('tour_id, date');

      const upcomingConcertCount = new Map<string, number>();
      const totalConcertCount = new Map<string, number>();
      const nearestShowDate = new Map<string, string>();
      const latestShowDate = new Map<string, string>();

      (allConcerts || []).forEach(c => {
        if (!c.tour_id) return;
        totalConcertCount.set(c.tour_id, (totalConcertCount.get(c.tour_id) || 0) + 1);
        if (!c.date) return;
        const cur = latestShowDate.get(c.tour_id);
        if (!cur || c.date > cur) latestShowDate.set(c.tour_id, c.date);
        if (c.date >= today) {
          upcomingConcertCount.set(c.tour_id, (upcomingConcertCount.get(c.tour_id) || 0) + 1);
          const n = nearestShowDate.get(c.tour_id);
          if (!n || c.date < n) nearestShowDate.set(c.tour_id, c.date);
        }
      });

      const { data: recentReviews } = await supabase
        .from('reviews')
        .select('tour_id, rating, id')
        .eq('status', 'published')
        .gte('created_at', thirtyDaysAgo.toISOString())
        .not('tour_id', 'is', null);

      const tourReviewData = new Map<string, { count: number; totalRating: number }>();
      (recentReviews || []).forEach(r => {
        const tourId = r.tour_id as string;
        const existing = tourReviewData.get(tourId) || { count: 0, totalRating: 0 };
        tourReviewData.set(tourId, { count: existing.count + 1, totalRating: existing.totalRating + (r.rating || 0) });
      });

      const reviewIds = (recentReviews || []).map(r => r.id);
      const { data: likes } = await supabase
        .from('user_likes')
        .select('review_id')
        .in('review_id', reviewIds.length ? reviewIds : ['00000000-0000-0000-0000-000000000000']);

      const reviewToTour = new Map<string, string>();
      (recentReviews || []).forEach(r => { if (r.tour_id) reviewToTour.set(r.id, r.tour_id as string); });
      const tourLikes = new Map<string, number>();
      (likes || []).forEach(l => {
        const tourId = reviewToTour.get(l.review_id || '');
        if (tourId) tourLikes.set(tourId, (tourLikes.get(tourId) || 0) + 1);
      });

      const tributePatterns = /tribute|cover\s*band|experience|celebration\s*of|salute\s*to|remembering|live\s*band\s*tribute/i;

      // Legacy / heritage acts: keep them discoverable elsewhere, but skip on the
      // homepage Featured Tours so the editorial surface feels current.
      const LEGACY_ACTS = new Set([
        'kenny chesney', 'ac/dc', 'acdc', 'bon jovi', 'aerosmith', 'kiss',
        'the rolling stones', 'rolling stones', 'eagles', 'fleetwood mac',
        'metallica', 'guns n roses', "guns n' roses", 'def leppard', 'motley crue',
        'mötley crüe', 'journey', 'foreigner', 'styx', 'reo speedwagon',
        'the who', 'who', 'pearl jam', 'rush', 'van halen', 'bruce springsteen',
        'billy joel', 'elton john', 'paul mccartney', 'the beach boys', 'beach boys',
        'chicago', 'santana', 'zz top', 'queen', 'pink floyd', 'roger waters',
        'iron maiden', 'judas priest', 'scorpions', 'whitesnake', 'lynyrd skynyrd',
        'george strait', 'reba mcentire', 'alan jackson', 'tim mcgraw', 'faith hill',
        'cher', 'madonna', 'rod stewart', 'james taylor', 'jimmy buffett',
        'phil collins', 'genesis', 'yes', 'styx', 'foreigner', 'doobie brothers',
        'the doobie brothers', 'steve miller band', 'boston', 'kansas',
      ]);
      const normalizeName = (n?: string | null) =>
        (n || '').toLowerCase().normalize('NFKD').replace(/[\u0300-\u036f]/g, '').trim();

      const scoredTours = tours
        .filter(t => {
          if (shouldExcludeArtist(t.artists?.name)) return false;
          if (tributePatterns.test(t.name)) return false;
          if (LEGACY_ACTS.has(normalizeName(t.artists?.name))) return false;
          return true;
        })
        .map(tour => {
          const upcoming = upcomingConcertCount.get(tour.id) || 0;
          const total = totalConcertCount.get(tour.id) || tour.concert_count || 0;
          const reviewData = tourReviewData.get(tour.id) || { count: 0, totalRating: 0 };
          const likeCount = tourLikes.get(tour.id) || 0;

          const ongoingBonus = Math.min(upcoming * 50, 250);
          const reviewScore = reviewData.count * 25;
          const likeScore = likeCount * 5;
          const ratingBonus = reviewData.count > 0
            ? (reviewData.totalRating / reviewData.count) * 10
            : (tour.average_rating || 0) * 5;
          const freshnessBonus = getFreshnessBonus(tour.created_at);
          const festivalPenalty = isFestivalTour(tour.name) ? -200 : 0;
          const concertBonus = Math.min(total * 3, 150);
          const totalScore = ongoingBonus + reviewScore + likeScore + ratingBonus + freshnessBonus + festivalPenalty + concertBonus;

          // Use artist image directly — no Spotify API calls
          const image = tour.image || tour.artists?.image || '';

          return {
            id: tour.id,
            name: tour.name,
            artistId: tour.artist_id,
            year: tour.year,
            averageRating: reviewData.count > 0 ? reviewData.totalRating / reviewData.count : tour.average_rating || 0,
            concertCount: total,
            reviewCount: reviewData.count,
            artistName: tour.artists?.name || '',
            genre: tour.artists?.genre || [],
            image,
            nearestShowDate: nearestShowDate.get(tour.id),
            latestShowDate: latestShowDate.get(tour.id),
            isOngoing: upcoming > 0,
            upcomingConcerts: upcoming,
            _score: totalScore,
          } as Tour & { _score: number; isOngoing: boolean; upcomingConcerts: number; nearestShowDate?: string; latestShowDate?: string; };
        })
        .sort((a, b) => {
          if (b._score !== a._score) return b._score - a._score;
          if (a.isOngoing !== b.isOngoing) return a.isOngoing ? -1 : 1;
          return (b.year || 0) - (a.year || 0);
        });

      let uniqueTours = deduplicateTours(scoredTours);
      uniqueTours = limitPerArtist(uniqueTours as any[], 1) as typeof uniqueTours;

      const topTours = uniqueTours.slice(0, 10);
      const restTours = uniqueTours.slice(10);
      const shuffledRest = seededShuffle(restTours, getWeeklySeed());
      uniqueTours = [...topTours, ...shuffledRest];

      return uniqueTours.slice(0, 100);
    },
    staleTime: 5 * 60 * 1000,
  });
};
